import _ from "lodash";
import { http } from "../Guard";
import { PaginationInput } from "./data.default";

// Paginated GET — replaces ~43 identical functions. `extraParams` injects endpoint-specific query
// params (e.g. a userUid) alongside the standard pagination/searchQuery serialization.
export function createPaginatedGet<T>(endpoint: string) {
  return (
    input: PaginationInput,
    extraParams?: Record<string, any>,
  ): Promise<T> => {
    const searchQuery = JSON.stringify(input.searchQuery);
    return http
      .get<T>(endpoint, {
        params: {
          ..._.omit(input, "searchQuery"),
          ...extraParams,
          searchQuery,
        },
      })
      .then(({ data }) => data);
  };
}

// Get by ID — replaces ~16 identical functions
export function createGetById<T>(endpoint: string) {
  return (uid: string): Promise<T> => {
    return http.get<T>(`${endpoint}/${uid}`).then(({ data }) => data);
  };
}

// Add (POST) — replaces ~38 identical functions
export function createAdd<TInput, TResult = any>(endpoint: string) {
  return (input: TInput): Promise<TResult> => {
    return http.post(endpoint, input).then(({ data }) => data);
  };
}

// Update (PATCH) — replaces ~33 identical functions
export function createUpdate<
  TInput extends { uid?: string },
  TResult = any,
>(endpoint: string) {
  return (input: TInput): Promise<TResult> => {
    return http
      .patch(`${endpoint}/${input.uid}`, _.omit(input, ["uid"]))
      .then(({ data }) => data);
  };
}

// Delete — replaces ~39 identical functions
export function createDelete(endpoint: string) {
  return (uid: string) => {
    return http.delete(`${endpoint}/${uid}`).then(({ data }) => data);
  };
}

// Admin brief 2026-11-03 — "archive, not delete". Soft-delete is the backend
// default; this helper fires /{endpoint}/{uid}/Restore to flip IsDeleted back.
export function createRestore(endpoint: string) {
  return (uid: string) => {
    return http.post(`${endpoint}/${uid}/Restore`).then(({ data }) => data);
  };
}

// Admin brief 2026-11-03 — paginated list of archived (soft-deleted) rows.
// Mirrors createPaginatedGet but hits the dedicated /{endpoint}/Archived
// endpoint which ignores the soft-delete query filter server-side.
export function createArchivedGet<T>(endpoint: string) {
  return (input: {
    searchQuery?: unknown;
    pageNumber: number;
    pageSize: number;
    sortBy?: string;
    sortDescending?: boolean;
  }): Promise<T> => {
    const searchQuery = input.searchQuery
      ? JSON.stringify(input.searchQuery)
      : undefined;
    return http
      .get<T>(`${endpoint.replace(/\/$/, "")}/Archived`, {
        params: {
          pageNumber: input.pageNumber,
          pageSize: input.pageSize,
          sortBy: input.sortBy,
          sortDescending: input.sortDescending,
          searchQuery,
        },
      })
      .then(({ data }) => data);
  };
}

// Assignment POST — POST `{base}/{entityUid}/{segment}/{targetUid}` for the assignment-row family
// (badges / rewards / wellbeing materials × Company/Department/User). Covers both payload styles the
// assignment controllers use: a JSON body (badges/rewards: assignment/expiration dates) and query
// params (materials: isRequired).
export function createAssignFn(
  base: string,
  segment: "Company" | "Department" | "User",
) {
  return (
    entityUid: string,
    targetUid: string,
    payload?: {
      readonly body?: any;
      readonly params?: Record<string, any>;
    },
  ) =>
    http
      .post(`${base}/${entityUid}/${segment}/${targetUid}`, payload?.body ?? null, {
        params: payload?.params,
      })
      .then(({ data }) => data);
}

// Trigger a browser "save file" of a blob payload under the given filename.
// Shared by the paginated export factory and single-file download factory below.
export function downloadBlob(data: BlobPart, filename: string): void {
  const link = document.createElement("a");
  const urlBlob = window.URL.createObjectURL(new Blob([data]));
  try {
    link.href = urlBlob;
    link.setAttribute("download", filename);
    document.body.appendChild(link);
    link.click();
  } finally {
    link.remove();
    window.URL.revokeObjectURL(urlBlob);
  }
}

// Export (blob download) — replaces ~34 identical functions
export function createExportFn(endpoint: string, filename: string) {
  return (input: PaginationInput) => {
    const searchQuery = JSON.stringify(input.searchQuery);
    return http
      .get(endpoint, {
        responseType: "blob",
        params: {
          ..._.omit(input, "searchQuery"),
          searchQuery,
        },
      })
      .then(({ data }) => downloadBlob(data, filename));
  };
}

// Single-file download from a GET-blob endpoint by uid. `buildUrl` resolves the
// per-file URL; the caller supplies the filename (the API streams raw bytes).
export function createFileDownload(buildUrl: (uid: string) => string) {
  return (uid: string, filename: string) =>
    http
      .get(buildUrl(uid), { responseType: "blob" })
      .then(({ data }) => downloadBlob(data, filename));
}
